import { ApiError } from '@gl/api-client';
import { useEffect } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { LocalContext } from '@/lib/local-context';
import { useLocalQuery } from '@/lib/locals';
import { setLastLocalId } from '@/lib/token';
import { cn } from '@/lib/utils';
import { LocalSwitcher } from './LocalSwitcher';
import { BOTTOM_TABS, SIDEBAR_NAV, visibleFor } from './nav';
import { UserMenu } from './UserMenu';

/**
 * Layout de `/l/:localId/*`. Mobile-first:
 * - < md: header compacto + bottom tab bar (Caja / Menú / Stats / Más).
 * - ≥ md: sidebar fija con toda la navegación.
 * Las páginas leen el local con `useCurrentLocal()`.
 */
export function AppShell() {
  const { localId } = useParams();
  const { user } = useAuth();
  const { data: local, error, isPending } = useLocalQuery(localId);

  useEffect(() => {
    if (local) setLastLocalId(local.id);
  }, [local]);

  if (isPending) return <FullScreenLoader />;
  if (error || !local) {
    const forbidden = error instanceof ApiError && (error.status === 403 || error.status === 404);
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-lg font-semibold">
          {forbidden ? 'No tenés acceso a este local' : 'No pudimos cargar el local'}
        </h1>
        <Button asChild>
          <Link to="/">Volver al inicio</Link>
        </Button>
      </div>
    );
  }

  const base = `/l/${local.id}`;
  const sidebarItems = SIDEBAR_NAV.filter(visibleFor(local.role));

  return (
    <LocalContext.Provider value={local}>
      <div className="min-h-dvh bg-background md:flex">
        {/* Sidebar (desktop) */}
        <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r bg-sidebar md:flex">
          <div className="border-b p-3">
            <LocalSwitcher current={local} />
          </div>
          <nav className="flex-1 space-y-1 overflow-y-auto p-3">
            {sidebarItems.map((item) => (
              <NavLink
                key={item.to}
                to={`${base}/${item.to}`}
                end={item.to === 'caja'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
                    isActive && 'bg-accent text-accent-foreground',
                  )
                }
              >
                <item.icon className="size-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3 border-t p-3">
            <UserMenu />
            <div className="min-w-0 text-xs">
              <p className="truncate font-medium">{user?.name}</p>
              <p className="truncate text-muted-foreground">{user?.isDemo ? 'Demo' : user?.email}</p>
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Header (mobile) */}
          <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur md:hidden">
            <LocalSwitcher current={local} className="flex-1" />
            <UserMenu />
          </header>

          {local.isDemo && (
            <div className="bg-amber-100 px-4 py-1.5 text-center text-xs text-amber-900">
              Estás en la demo: los datos se reinician todas las noches.
            </div>
          )}

          <main className="mx-auto w-full max-w-6xl flex-1 px-4 pt-4 pb-24 md:px-8 md:pt-8 md:pb-10">
            {/* key: al cambiar de local se remontan las páginas (forms con defaultValues del local) */}
            <Outlet key={local.id} />
          </main>
        </div>

        {/* Bottom tab bar (mobile) */}
        <nav className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur md:hidden">
          <ul className="grid grid-cols-4">
            {BOTTOM_TABS.map((tab) => (
              <li key={tab.to}>
                <NavLink
                  to={`${base}/${tab.to}`}
                  end={tab.to === 'caja'}
                  className={({ isActive }) =>
                    cn(
                      'flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted-foreground',
                      isActive && 'text-primary',
                    )
                  }
                >
                  <tab.icon className="size-5" />
                  {tab.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </LocalContext.Provider>
  );
}
